import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFnsV3";
import { useCallback, useRef, useState } from "react";
import { fr } from "date-fns/locale/fr";
import MaterialUiThemeProvider from "../../../providers/MaterialUiThemeProvider";
import { colors } from "../../../styles/figmaColors";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarWeek } from "../../materialUi/materialUi";
import { styled } from "styled-components";
import { TextCapitalized } from "../text/TextCapitalized";

type MaterialUiSingleDatePickerProps = {
  value: Date | null;
  onChange: (date: Date) => void;
  title?: string;
};

export const MaterialUiSingleDatePicker = ({
  onChange,
  value,
  title,
}: MaterialUiSingleDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const isOpeningRef = useRef(false);
  const [changeValue, setChangeValue] = useState<Date | null>(null);

  const handleChange = useCallback((date: Date | null) => {
    setChangeValue(date);
  }, []);

  const handleAccept = useCallback(
    (date: Date | null) => {
      if (!date) {
        return;
      }
      date.setHours(12);
      onChange(date);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <MaterialUiThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <DatePicker
          value={value}
          maxDate={new Date()}
          onAccept={handleAccept}
          onChange={handleChange}
          open={open}
          label={<TextCapitalized>{title ?? ""}</TextCapitalized>}
          onOpen={() => {
            isOpeningRef.current = true;
            setOpen(true);
          }}
          onClose={() => {
            isOpeningRef.current = false;
            setOpen(false);
          }}
          slots={{
            openPickerIcon: () => (
              <StyledIcon>
                <CalendarWeek />
              </StyledIcon>
            ),
          }}
          sx={{
            "& .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
              outline: "none",
              border: `0.0625rem solid ${colors["colors/borders/cells/cells"]} !important`,
              boxShadow: "2px 1px 4px 0px rgba(122, 122, 122, 0.08)",
            },
            "& .MuiInputBase-root .MuiOutlinedInput-root:hover ": {
              backgroundColor: `${colors["colors/button/secondary/hover"]} !important`,
            },
            "& .MuiInputBase-input-MuiOutlinedInput-input": {
              minWidth: "17rem",
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
              backgroundColor: `white`,
            },
          }}
          slotProps={{
            textField: {
              onBlur: () => {
                if (isOpeningRef.current) return;
                if (!changeValue) return;
                handleAccept(changeValue);
              },
              inputProps: {
                onClick: () => {
                  isOpeningRef.current = true;
                  setOpen(true);
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </MaterialUiThemeProvider>
  );
};

const StyledIcon = styled.div`
  & svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: ${colors["colors/text/black"]} !important;
  }
`;

export default { MaterialUiSingleDatePicker };
